import React from 'react';
import { Button, HStack, Icon, Text } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import { LuChevronRight, LuMaximize2, LuMinimize2 } from 'react-icons/lu';
import { SegmentGroup } from '@/components/ui/segment-group';
import {
	COMPOSER_TABS,
	COMPOSER_TAB_LABEL,
	COMPOSER_TOOLTIP_CLOSE_DELAY_MS,
	COMPOSER_TOOLTIP_OPEN_DELAY_MS,
	ComposerTab,
} from './composerConstants.ts';
import ComposerUploadHelp from './ComposerUploadHelp.tsx';
import { Tooltip } from '@/components/ui/tooltip.tsx';

interface ComposerTabStripProps {
	activeTab: ComposerTab;
	onSelect: (tab: ComposerTab) => void;
	/** The sheet supplies its own "Add" header, so it hides the inline label. */
	showLabel?: boolean;
	onOverlayOpenChange?: (isOpen: boolean) => void;
	tabs?: readonly ComposerTab[];
	dirtyTabs?: readonly ComposerTab[];
	isExpanded?: boolean;
	onToggleExpanded?: () => void;
	/** Empties the active tab's draft. Only offered while that tab is dirty. */
	onClear?: () => void;
}

/**
 * "Add" label + entry-type segmented selector (Note or file / Task). Replaces the earlier pill row;
 * the segmented control makes the single active choice clearer and removes the manual divider.
 *
 * With one tab, as permissions can leave it, the selector is replaced by that tab's name: a segment
 * that cannot change reads as a choice that is not there.
 */
const ComposerTabStrip: React.FC<ComposerTabStripProps> = (props) => {
	const {
		activeTab,
		onSelect,
		showLabel = true,
		onOverlayOpenChange,
		tabs = COMPOSER_TABS,
		dirtyTabs = [],
		isExpanded = false,
		onToggleExpanded,
		onClear,
	} = props;
	const canClear = Boolean(onClear) && dirtyTabs.includes(activeTab);
	const tabOptions = tabs.map((tab) => ({
		label: COMPOSER_TAB_LABEL[tab],
		value: tab,
		isDirty: dirtyTabs.includes(tab),
	}));

	return (
		<HStack px={4} py={3} gap={3} align="center">
			{showLabel && (
				<Text fontSize="sm" fontWeight="semibold" flexShrink={0}>
					Add
				</Text>
			)}
			<HStack flex={1} minW={0} justify="flex-start">
				{tabs.length === 1 ? (
					// Reads "Add > Note or file". The pulled-in chevron sits evenly between the two words.
					<HStack gap={1.5} minW={0}>
						{showLabel && (
							<Icon as={LuChevronRight} boxSize={3.5} color="fg.subtle" ml={-1.5} aria-hidden />
						)}
						<Text fontSize="sm" fontWeight="medium" color="fg.muted" truncate>
							{COMPOSER_TAB_LABEL[tabs[0]]}
						</Text>
					</HStack>
				) : (
					<SegmentGroup
						key={tabs.join('|')}
						options={tabOptions}
						value={activeTab}
						onValueChange={({ value }) => value && onSelect(value as ComposerTab)}
						size="sm"
						fontSize="xs"
						fontWeight="medium"
						// "Note or file" would otherwise wrap inside its pill when the row is tight.
						noWrap
						w="auto"
						indicatorBg="surface.default"
					/>
				)}
			</HStack>
			<HStack gap={1} flexShrink={0}>
				{canClear && (
					<Button
						type="button"
						variant="ghost"
						size="sm"
						h={8}
						px={3}
						rounded="full"
						color="fg.muted"
						textStyle="label.sm"
						onClick={onClear}
					>
						Clear
					</Button>
				)}
				{activeTab === ComposerTab.Record && <ComposerUploadHelp onOpenChange={onOverlayOpenChange} />}
				{onToggleExpanded && (
					<Tooltip
						openDelay={COMPOSER_TOOLTIP_OPEN_DELAY_MS}
						closeDelay={COMPOSER_TOOLTIP_CLOSE_DELAY_MS}
						content={isExpanded ? 'Collapse composer' : 'Expand composer'}
						positioning={{ placement: 'top' }}
					>
						<IconButton
							type="button"
							aria-label={isExpanded ? 'Collapse composer' : 'Expand composer'}
							aria-pressed={isExpanded}
							variant="ghost"
							size="sm"
							color="fg.muted"
							boxSize={8}
							minW={8}
							onClick={onToggleExpanded}
						>
							{isExpanded ? <LuMinimize2 /> : <LuMaximize2 />}
						</IconButton>
					</Tooltip>
				)}
			</HStack>
		</HStack>
	);
};

export default ComposerTabStrip;
